import type { AgentEvent, AgentProvider } from "@pluck/desktop-bridge";

export type ChatItem =
	| { id: string; kind: "user" | "assistant" | "error"; text: string }
	| {
			id: string;
			kind: "tool";
			name: string;
			status: "started" | "done" | "error";
			/** Input while running, result once finished. */
			detail?: string;
	  };

export interface Chat {
	createdAt: number;
	id: string;
	items: ChatItem[];
	provider: AgentProvider;
	/** The agent's own session id, used to resume this chat. */
	providerSessionId?: string;
	title: string;
	updatedAt: number;
}

const STORAGE_PREFIX = "pluck.chats.v1.";
const MAX_CHATS_PER_BOARD = 30;
const TITLE_CHARS = 48;

let counter = 0;
export function newId(prefix: string): string {
	counter += 1;
	return `${prefix}-${Date.now().toString(36)}-${counter.toString(36)}`;
}

export function createChat(provider: AgentProvider): Chat {
	const now = Date.now();
	return {
		id: newId("chat"),
		provider,
		title: "New chat",
		items: [],
		createdAt: now,
		updatedAt: now,
	};
}

export function addUserMessage(chat: Chat, text: string): Chat {
	const title =
		chat.items.length === 0
			? text.trim().replace(/\s+/g, " ").slice(0, TITLE_CHARS) || chat.title
			: chat.title;
	return {
		...chat,
		title,
		updatedAt: Date.now(),
		items: [...chat.items, { id: newId("msg"), kind: "user", text }],
	};
}

/** Fold one agent event into the chat transcript. */
export function applyAgentEvent(chat: Chat, event: AgentEvent): Chat {
	switch (event.type) {
		case "session":
			return { ...chat, providerSessionId: event.providerSessionId };
		case "text":
			return {
				...chat,
				updatedAt: Date.now(),
				items: [
					...chat.items,
					{ id: newId("msg"), kind: "assistant", text: event.text },
				],
			};
		case "error":
			return {
				...chat,
				updatedAt: Date.now(),
				items: [
					...chat.items,
					{ id: newId("msg"), kind: "error", text: event.message },
				],
			};
		case "tool": {
			if (event.status === "started") {
				return {
					...chat,
					items: [
						...chat.items,
						{
							id: event.id,
							kind: "tool",
							name: event.name,
							status: "started",
							detail: event.detail,
						},
					],
				};
			}
			return {
				...chat,
				items: chat.items.map((item) =>
					item.kind === "tool" && item.id === event.id
						? {
								...item,
								status: event.status,
								detail: event.detail ?? item.detail,
							}
						: item
				),
			};
		}
		default:
			return chat;
	}
}

/** Tools still marked running when a turn ends were interrupted. */
export function settleRunningTools(chat: Chat): Chat {
	return {
		...chat,
		items: chat.items.map((item) =>
			item.kind === "tool" && item.status === "started"
				? { ...item, status: "error" as const }
				: item
		),
	};
}

export function loadChats(boardId: string): Chat[] {
	try {
		const raw = localStorage.getItem(STORAGE_PREFIX + boardId);
		const parsed = raw ? (JSON.parse(raw) as Chat[]) : [];
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

export function saveChats(boardId: string, chats: Chat[]): void {
	try {
		const recent = [...chats]
			.sort((a, b) => b.updatedAt - a.updatedAt)
			.slice(0, MAX_CHATS_PER_BOARD);
		localStorage.setItem(STORAGE_PREFIX + boardId, JSON.stringify(recent));
	} catch {
		// Storage full or unavailable; chats just won't persist.
	}
}
